import { inject, Injectable } from '@angular/core';
import {
  BackOfficeApplicationsScheduleFilesToDeletePayload,
  BackOfficeApplicationsRestorePayload,
  BackOfficeApplicationsSoftDeletePayload,
  BackOfficeGenerateApplicationReport,
  BackOfficeGenerateTransactionsReport,
  BackOfficeGetApplicationsPayload,
  BackOfficeGetPartnersPayload,
  BackOfficeGetScheduledFilesDeletionPayload,
  BackOfficeGetTransactionsPayload,
  BackOfficeGetUsersPayload,
  BackOfficeUsersActivateAccountPayload,
  BackOfficeUsersChangePasswordPayload,
  BackOfficeUsersChangePasswordReturn,
  BackOfficeUsersDeactivateAccountPayload,
  BackOfficeUsersPermDeletePayload,
  BackOfficeUsersRestoreAccountPayload,
  BackOfficeUsersSoftDeletePayload,
  Company,
  Language,
  PaginatedResponse,
  Purchase,
  Role,
  User,
  UserAsset,
  UserDocument,
  UserProcess,
  BackOfficeGenerateUsersReport,
} from '@interfaces';
import { Observable } from 'rxjs';
import { environment } from '@environment';
import { HttpClient } from '@angular/common/http';
import { ScheduledFileDeletion } from '../../_interfaces/scheduled-file-deletion';

@Injectable({
  providedIn: 'root',
})
export class BackOfficeHttpService {
  private readonly http = inject(HttpClient);

  private API_URL = environment.API_URL;

  public getRoles(): Observable<Role[]> {
    const url = `${this.API_URL}/back-office/roles`;
    return this.http.get<Role[]>(url);
  }

  public getLanguages(): Observable<Language[]> {
    const url = `${this.API_URL}/back-office/languages`;
    return this.http.get<Language[]>(url);
  }

  public getCompanies(): Observable<Company[]> {
    const url = `${this.API_URL}/back-office/companies`;
    return this.http.get<Company[]>(url);
  }

  public getEmployees(): Observable<User[]> {
    const url = `${this.API_URL}/back-office/employees`;
    return this.http.get<User[]>(url);
  }

  public getPartners(payload: BackOfficeGetPartnersPayload): Observable<Partial<User>[]> {
    const url = `${this.API_URL}/back-office/partners/search`;
    return this.http.post<Partial<User>[]>(url, payload);
  }

  // ############
  // USERS
  // ############

  public getUsers(payload: BackOfficeGetUsersPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/back-office/users/search`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }

  public usersActivateAccount(
    payload: BackOfficeUsersActivateAccountPayload
  ): Observable<{ activatedAccountID: string }> {
    const { userID, password, passwordConfirm } = payload;
    const url = `${this.API_URL}/back-office/users/${userID}/activate`;
    return this.http.post<{ activatedAccountID: string }>(url, { password, passwordConfirm });
  }

  public usersDeactivateAccount(
    payload: BackOfficeUsersDeactivateAccountPayload
  ): Observable<{ deactivatedAccountID: string }> {
    const url = `${this.API_URL}/back-office/users/${payload.userID}/deactivate`;
    return this.http.get<{ deactivatedAccountID: string }>(url);
  }

  public usersRestoreAccount(payload: BackOfficeUsersRestoreAccountPayload): Observable<{ restoredAccountID: string }> {
    const url = `${this.API_URL}/back-office/users/${payload.userID}/restore`;
    return this.http.get<{ restoredAccountID: string }>(url);
  }

  public usersChangePassword(
    payload: BackOfficeUsersChangePasswordPayload
  ): Observable<BackOfficeUsersChangePasswordReturn> {
    const { userID, mode, password, passwordConfirm } = payload;
    const url = `${this.API_URL}/back-office/users/${userID}/change-password`;
    return this.http.post<BackOfficeUsersChangePasswordReturn>(url, { mode, password, passwordConfirm });
  }

  public usersSoftDelete(payload: BackOfficeUsersSoftDeletePayload): Observable<{ deletedUsersID: string[] }> {
    const url = `${this.API_URL}/back-office/users/soft-delete`;
    return this.http.delete<{ deletedUsersID: string[] }>(url, { body: payload });
  }

  public usersPermDelete(payload: BackOfficeUsersPermDeletePayload): Observable<{ deletedUsersID: string[] }> {
    const url = `${this.API_URL}/back-office/users/perm-delete`;
    return this.http.delete<{ deletedUsersID: string[] }>(url, { body: payload });
  }

  public usersGenerateReport(payload: BackOfficeGenerateUsersReport): Observable<any> {
    const url = `${this.API_URL}/back-office/users/generate-report`;
    return this.http.post(url, payload, { responseType: 'blob', observe: 'response' });
  }

  // ############
  // TRANSACTION
  // ############

  public getTransactions(payload: BackOfficeGetTransactionsPayload): Observable<PaginatedResponse<Purchase>> {
    const url = `${this.API_URL}/back-office/transactions/search/`;
    return this.http.post<PaginatedResponse<Purchase>>(url, payload);
  }

  public downloadPurchaseDocument(opts: { transactionID: string }): Observable<any> {
    const url = `${this.API_URL}/back-office/transactions/${opts.transactionID}/purchase-document`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  public generateTransactionsReport(payload: BackOfficeGenerateTransactionsReport): Observable<any> {
    const url = `${this.API_URL}/back-office/transactions/generate-report`;
    return this.http.post(url, payload, { responseType: 'blob', observe: 'response' });
  }

  // ############
  // APPLICATIONS
  // ############

  public getApplications(payload: BackOfficeGetApplicationsPayload): Observable<PaginatedResponse<UserProcess>> {
    const url = `${this.API_URL}/back-office/applications/search/`;
    return this.http.post<PaginatedResponse<UserProcess>>(url, payload);
  }

  public generateApplicationsReport(payload: BackOfficeGenerateApplicationReport): Observable<any> {
    const url = `${this.API_URL}/back-office/applications/generate-report`;
    return this.http.post(url, payload, { responseType: 'blob', observe: 'response' });
  }

  public applicationsSoftDelete(
    payload: BackOfficeApplicationsSoftDeletePayload
  ): Observable<{ deletedApplicationsID: string[] }> {
    const url = `${this.API_URL}/back-office/applications/soft-delete`;
    return this.http.delete<{ deletedApplicationsID: string[] }>(url, { body: payload });
  }

  public applicationsRestore(
    payload: BackOfficeApplicationsRestorePayload
  ): Observable<{ deletedApplicationID: string }> {
    const url = `${this.API_URL}/back-office/applications/restore`;
    return this.http.post<{ deletedApplicationID: string }>(url, payload);
  }

  public scheduleUserProcessesFilesToDelete(
    payload: BackOfficeApplicationsScheduleFilesToDeletePayload
  ): Observable<{ scheduledUserProcessesFilesToDelete: string[] }> {
    const url = `${this.API_URL}/back-office/applications/schedule-files-to-delete`;
    return this.http.delete<{ scheduledUserProcessesFilesToDelete: string[] }>(url, { body: payload });
  }

  // ############
  // SCHEDULED FILES DELETION
  // ############

  public getScheduledFilesDeletion(
    payload: BackOfficeGetScheduledFilesDeletionPayload
  ): Observable<PaginatedResponse<ScheduledFileDeletion>> {
    const url = `${this.API_URL}/back-office/scheduled-files-deletion/search/`;
    return this.http.post<PaginatedResponse<ScheduledFileDeletion>>(url, payload);
  }

  public deleteScheduledFiles(payload: {
    filesIdsToDelete: string[];
  }): Observable<{ scheduledFileDeletion: ScheduledFileDeletion[] }> {
    const url = `${this.API_URL}/back-office/scheduled-files-deletion`;
    return this.http.delete<{ scheduledFileDeletion: ScheduledFileDeletion[] }>(url, { body: payload });
  }

  public restoreScheduledFiles(payload: {
    filesIdsToRestore: string[];
  }): Observable<{ scheduledFileDeletion: ScheduledFileDeletion[] }> {
    const url = `${this.API_URL}/back-office/scheduled-files-deletion/restore`;
    return this.http.post<{ scheduledFileDeletion: ScheduledFileDeletion[] }>(url, payload);
  }

  // ############
  // MANAGE FILES
  // ############

  public getUserProcessBasicInformation(userProcessId: string): Observable<{
    userProcess: UserProcess;
  }> {
    const url = `${this.API_URL}/back-office/manage-files/user-process/${userProcessId}/details`;
    return this.http.get<{
      userProcess: UserProcess;
    }>(url);
  }

  public getUserProcessManageFiles(userProcessId: string): Observable<{
    userDocuments: UserDocument[];
    userAssets: UserAsset[];
  }> {
    const url = `${this.API_URL}/back-office/manage-files/user-process/${userProcessId}`;
    return this.http.get<{
      userDocuments: UserDocument[];
      userAssets: UserAsset[];
    }>(url);
  }

  public manageFilesDownload(payload: {
    userProcessId: string;
    fileId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): Observable<any> {
    let url = `${this.API_URL}/back-office/manage-files/${payload.userProcessId}/${payload.fileId}/${payload.fileType}/download`;
    if (payload.fileName) {
      url += `/${encodeURIComponent(payload.fileName)}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  public manageFilesDelete(payload: {
    userProcessId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): Observable<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }> {
    const url = `${this.API_URL}/back-office/manage-files/file`;
    return this.http.delete<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }>(url, {
      body: payload,
    });
  }

  public manageFilesDeleteAll(payload: {
    userProcessId: string;
  }): Observable<{ scheduledFilesDeletion: ScheduledFileDeletion[] }> {
    const url = `${this.API_URL}/back-office/manage-files/files/all`;
    return this.http.delete<{ scheduledFilesDeletion: ScheduledFileDeletion[] }>(url, {
      body: payload,
    });
  }

  public manageFilesRestore(payload: {
    userProcessId: string;
    fileName: string;
    fileType: 'userDocuments' | 'userAssets';
  }): Observable<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }> {
    const url = `${this.API_URL}/back-office/manage-files/file/restore`;
    return this.http.post<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }>(url, payload);
  }

  public manageFilesRestoreAll(payload: {
    userProcessId: string;
  }): Observable<{ success: boolean; deletedCount: number }> {
    const url = `${this.API_URL}/back-office/manage-files/files/restore/all`;
    return this.http.post<{ success: boolean; deletedCount: number }>(url, payload);
  }
}
